/**
 * Shift-related utility functions shared across shift pages and components.
 */

import { getCurrencyDecimals, majorToAmount } from '@bbpos/utils';
import { tenantNow } from '@/lib/tenant-clock';

/** Format HH:mm military time as 12-hour with AM/PM. */
export function formatTime12h(time24: string): string {
  const [hh, mm] = time24.split(':').map(Number);
  const suffix = hh >= 12 ? 'PM' : 'AM';
  const h = hh % 12 || 12;
  return `${h}:${String(mm).padStart(2, '0')} ${suffix}`;
}

/** Format elapsed minutes as HH:MM. */
export function formatElapsed(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** "0h 30m" — unambiguous duration (formatElapsed's hh:mm reads like a clock time). */
export function formatElapsedHuman(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m}m`;
}

function toMinutes(time: string): number {
  const [hh, mm] = time.split(':').map(Number);
  return hh * 60 + mm;
}

/** An overnight shift (e.g. 22:00–06:00) ends the day after it starts. */
function isOvernight(startTime: string, endTime: string): boolean {
  return toMinutes(endTime) < toMinutes(startTime);
}

/**
 * Calculate overtime minutes past shift end time. Returns 0 if not overtime.
 * With the shift start and the clock-in, the end is placed on the clock-in's
 * day — or the next day for an overnight shift clocked in before midnight.
 *
 * Shift times are the store's clock (T14), not the device's: now and the
 * clock-in are both read as wall clock in `locationId`'s zone (default the
 * till's active store), so the difference is taken in one frame.
 */
export function getOvertimeMinutes(
  endTime: string,
  startTime?: string,
  clockIn?: string,
  locationId?: string | null,
): number {
  const now = tenantNow(new Date(), locationId);
  const [hh, mm] = endTime.split(':').map(Number);
  const shiftEnd = tenantNow(clockIn ? new Date(clockIn) : new Date(), locationId);
  const clockInMinutes = shiftEnd.getHours() * 60 + shiftEnd.getMinutes();
  shiftEnd.setHours(hh, mm, 0, 0);
  if (clockIn && startTime && isOvernight(startTime, endTime) && clockInMinutes >= toMinutes(endTime)) {
    shiftEnd.setDate(shiftEnd.getDate() + 1);
  }
  const diff = now.getTime() - shiftEnd.getTime();
  return diff > 0 ? Math.floor(diff / 60000) : 0;
}

/**
 * The shift's start as a store wall-clock Date (T14): today's, or yesterday's
 * when now is in the after-midnight part of an overnight shift.
 */
function shiftStartFor(now: Date, startTime: string, endTime?: string): Date {
  const [hh, mm] = startTime.split(':').map(Number);
  const shiftStart = new Date(now.getTime());
  shiftStart.setHours(hh, mm, 0, 0);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  if (endTime && isOvernight(startTime, endTime) && nowMinutes < toMinutes(endTime)) {
    shiftStart.setDate(shiftStart.getDate() - 1);
  }
  return shiftStart;
}

/** Check if the store's current time is >15 minutes past shift start. */
export function isLateForShift(startTime: string, endTime?: string, locationId?: string | null): boolean {
  const now = tenantNow(new Date(), locationId);
  return now.getTime() - shiftStartFor(now, startTime, endTime).getTime() > 15 * 60 * 1000;
}

/** Check if the store's current time is before shift start. */
export function isBeforeShift(startTime: string, endTime?: string, locationId?: string | null): boolean {
  const now = tenantNow(new Date(), locationId);
  return now.getTime() < shiftStartFor(now, startTime, endTime).getTime();
}

/** Get today's date as YYYY-MM-DD in local timezone (not UTC). */
export function getLocalDateString(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * The typed opening cash float in minor units: blank is 0 (no float); anything
 * but a non-negative amount with at most the currency's decimals is null.
 */
export function parseOpeningFloat(input: string, currency: string): number | null {
  const text = input.trim();
  if (!text) return 0;
  const decimals = getCurrencyDecimals(currency);
  const pattern = decimals > 0 ? new RegExp(`^\\d+(\\.\\d{0,${decimals}})?$`) : /^\d+$/;
  if (!pattern.test(text)) return null;
  return majorToAmount(Number(text), currency);
}
